import { useMemo, useRef, type CSSProperties } from "react";
import { Vector3 } from "three";
import { CUBE_RADIUS, sphericalToDirection, useTourState } from "@heroncendre/player";
import { useProjectionHotspots } from "./useProjectionHotspots.js";
import "./pulseHotspots.css";

/**
 * ============================================================================
 *  PulseHotspots.tsx — Un souffle de lumière sur les points cliquables
 * ----------------------------------------------------------------------------
 *  Le moteur ne fait pulser un marqueur qu'au SURVOL : au repos, les points sont
 *  calmes, et sur téléphone — pas de survol — ils ne le sont jamais autrement.
 *  Un visiteur qui ne sait pas qu'il peut cliquer ne cherche pas. Un halo discret
 *  grossit et s'éclaire doucement derrière chaque point (`pulseHotspots.css`).
 *
 *  Les marqueurs sont dessinés dans le canvas du moteur, hors de portée de la
 *  coquille : on ne peut pas les faire grossir eux-mêmes. Le halo est du DOM posé
 *  sur eux (projection : `useProjectionHotspots`), à leur taille à l'écran, et
 *  ne capte aucun geste.
 *
 *  Les points au sol (`render: "floor"`) sont des anneaux couchés sur le plan :
 *  un halo rond de face n'y ressemblerait pas, ils n'en ont pas.
 *
 *  Écart avec le moteur : à un changement de scène les halos ne s'effacent pas en
 *  fondu avec les points (`HotspotPresence` n'est pas lisible d'ici) ; ils suivent
 *  la scène courante.
 * ==========================================================================*/

/** Les constantes du moteur (`Hotspots3D`) : distance des marqueurs, taille d'un panneau en visite. */
const DISTANCE_MARQUEUR = CUBE_RADIUS * 0.8;
const TAILLE_MARQUEUR = 0.62;
/** Côté du halo de base (`.ph-halo`) : l'échelle appliquée le ramène à la taille du marqueur. */
const BASE_PX = 80;

export function PulseHotspots() {
  const tour = useTourState((s) => s.tour);
  const sceneId = useTourState((s) => s.currentSceneId);
  const cadre = useRef<HTMLDivElement>(null);
  const elements = useRef(new Map<string, HTMLDivElement>());

  const points = useMemo(() => {
    const scene = tour.scenes.find((s) => s.id === sceneId);
    return (scene?.hotspots ?? [])
      .filter((h) => !(h.type === "nav" && (h.render === "floor" || !tour.scenes.some((s) => s.id === h.target))))
      .map((h) => ({ id: h.id, direction: sphericalToDirection(h.position, new Vector3()) }));
  }, [tour, sceneId]);

  useProjectionHotspots(cadre, points, true, (id, ecran, { height, fov }) => {
    const element = elements.current.get(id);
    if (!element) return;
    if (!ecran) {
      element.style.visibility = "hidden";
      return;
    }
    // Taille à l'écran du marqueur : son côté en unités du monde, à sa distance, sous le champ vertical.
    const marqueurPx = (TAILLE_MARQUEUR / DISTANCE_MARQUEUR) * (height / 2) / Math.tan((fov * Math.PI) / 360);
    element.style.visibility = "visible";
    element.style.transform = `translate3d(${ecran.x}px, ${ecran.y}px, 0) translate(-50%, -50%) scale(${(marqueurPx / BASE_PX).toFixed(4)})`;
  });

  return (
    <div ref={cadre} aria-hidden style={calque}>
      {points.map((point) => (
        <div
          key={point.id}
          ref={(element) => {
            if (element) elements.current.set(point.id, element);
            else elements.current.delete(point.id);
          }}
          style={ancre}
        >
          <div className="ph-halo" />
        </div>
      ))}
    </div>
  );
}

const calque: CSSProperties = { position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" };

/** Positionnée par la projection ; l'animation est sur l'enfant, pour que les deux transformations ne se battent pas. */
const ancre: CSSProperties = { position: "absolute", top: 0, left: 0, visibility: "hidden", pointerEvents: "none" };
